import axios from "axios";
import { API_URL } from "constants.js";
import { post as signWithClick, declineSignRequest as declineClick } from "services/clicktosign.service";
import { declineDokobitSignRequest as declineDokobit } from "services/dokobit.service";
import { updateStatus } from "services/reviewfiles.service";
import { ReviewQueueItem, SignQueueItem } from "services/cabinet-queues.service";
import { clickSignErrorKey } from "utils/click-sign-errors";

/**
 * What the buttons of the sheet send. Every request is made for the document's own
 * company (the list spans all of the person's companies, the page is only one of them).
 * A request that succeeds returns the translation key of the line to show; one that
 * fails throws, and `failureMessageKey` turns the error into the line to show.
 */

export async function signCurrentWithClick(item: SignQueueItem): Promise<string> {
  const answer = await signWithClick(item.companyId, { fileId: item.fileId });
  // A `message` in the answer says the manager was asked to sign next.
  return answer?.message ? "queue_msg_signed_manager_asked" : "queue_msg_signed";
}

export async function declineSigning(item: SignQueueItem, declineReason: string): Promise<string> {
  const decline = item.signType === "Dokobit" ? declineDokobit : declineClick;
  await decline(item.companyId, { fileId: item.fileId, declineReason });
  return "queue_msg_declined";
}

export async function markAsReviewed(item: ReviewQueueItem): Promise<string> {
  try {
    await updateStatus(item.id, {});
  } catch (error) {
    // Reviewed already, or the file is gone: either way there is nothing left to do here.
    if (!isReviewRowGone(error)) throw error;
  }
  return "queue_msg_reviewed";
}

/** Dokobit signs in its own page, so the address opens in a new tab. Call it straight from the click, or the popup blocker stops it. */
export function openDokobitSigning(item: SignQueueItem): boolean {
  const url = `${API_URL}/${item.companyId}/dokobit/fetch-sign-url/file/${item.fileId}`;
  return window.open(url, "_blank") !== null;
}

// The text of a 400 answer: the review route sends a bare string, the sign routes `{ message }` or `{ error }`.
function answerOf(error: unknown): { message?: string; error?: string } {
  const data = axios.isAxiosError(error) ? error.response?.data : undefined;
  return typeof data === "string" ? { message: data } : data ?? {};
}

function isReviewRowGone(error: unknown): boolean {
  return (
    axios.isAxiosError(error) && error.response?.status === 400 && answerOf(error).message === "REVIEW_FILE_NOT_FOUND"
  );
}

/**
 * The line for a failed sign, decline or review request. An answer with an `error` field
 * means the request itself was declined, cancelled or withdrawn; `owner_must_sign_first`
 * has its own text; anything not listed is the generic line.
 */
export function failureMessageKey(error: unknown): string {
  const { message, error: errorField } = answerOf(error);
  switch (message) {
    case "document_annulled":
      return "queue_error_annulled";
    case "document_withdrawn":
      return "queue_error_withdrawn";
    case "signature_state_changed":
      return "queue_error_changed";
  }
  if (errorField) return "queue_error_changed";
  return clickSignErrorKey(error, "queue_error_generic");
}
